import React from "react";
import { Link } from "react-router-dom";
import { Quote, Star, ArrowRight, Phone } from "lucide-react";
import { Button } from "../components/ui/button";
import { testimonials, companyInfo } from "../data/mockData";
import TestimonialCard from "../components/TestimonialCard";

const Testimonials = () => {
  const featured = testimonials[0];
  const rest = testimonials.slice(1);

  return (
    <div data-testid="testimonials-page" className="bg-stone-50">
      {/* HERO */}
      <section className="relative bg-amber-50/30 py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl animate-fade-up">
            <p className="text-amber-700 text-sm uppercase tracking-[0.3em] font-semibold mb-5">
              Testimonials
            </p>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-slate-900 leading-[1.05] tracking-tight">
              Words from the families{" "}
              <span className="italic text-amber-700">we serve.</span>
            </h1>
            <p className="mt-8 text-lg lg:text-xl text-slate-600 leading-relaxed max-w-2xl">
              These are real stories from real families who invited us into their homes — and into their hearts.
            </p>
          </div>
        </div>
      </section>

      {/* FEATURED */}
      <section className="py-20 lg:py-28">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-slate-900 text-white rounded-[2.5rem] overflow-hidden">
            <div className="p-10 lg:p-16">
              <Quote className="w-16 h-16 text-amber-500/30 mb-6" strokeWidth={1.5} />
              <div className="flex gap-1 mb-8">
                {Array.from({ length: featured.rating }).map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-amber-400 fill-amber-400" />
                ))}
              </div>
              <p className="font-serif text-2xl lg:text-4xl leading-relaxed text-white mb-10 italic">
                "{featured.quote}"
              </p>
              <div className="pt-8 border-t border-stone-800">
                <p className="font-serif text-2xl text-amber-400">{featured.name}</p>
                <p className="text-stone-400 mt-1">
                  {featured.relationship} &middot; {featured.location}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS GRID */}
      <section className="pb-20 lg:pb-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {rest.map((t) => (
              <TestimonialCard key={t.id} testimonial={t} />
            ))}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="py-16 lg:py-20 bg-amber-50/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            {[
              { number: "500+", label: "Families served" },
              { number: "4.9", label: "Average rating" },
              { number: "98%", label: "Would recommend" },
              { number: "24/7", label: "Always available" }
            ].map((stat, i) => (
              <div key={i} data-testid={`stat-${i}`}>
                <p className="font-serif text-5xl lg:text-6xl text-amber-700 mb-2">
                  {stat.number}
                </p>
                <p className="text-slate-600 text-sm uppercase tracking-widest">
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-slate-900 text-white py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="font-script text-4xl text-amber-400 mb-5">Become part of our family.</p>
          <h2 className="font-serif text-4xl sm:text-5xl text-white leading-tight mb-8">
            Your story deserves this kind of care.
          </h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`tel:${companyInfo.phone.replace(/\D/g, "")}`}>
              <Button className="bg-amber-600 hover:bg-amber-700 text-white rounded-full px-8 py-6 w-full sm:w-auto">
                <Phone className="w-4 h-4 mr-2" />
                {companyInfo.phone}
              </Button>
            </a>
            <Link to="/contact">
              <Button
                variant="outline"
                className="border-2 border-white text-white bg-transparent hover:bg-white hover:text-slate-900 rounded-full px-8 py-6 w-full sm:w-auto"
              >
                Request a consultation
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Testimonials;
